import footprint, { FootprintComponentKind } from "@onefootprint/footprint-js";
import { queriesBiometricIdentityVerifications } from "@query";
import {
  BiometricIdentityVerificationRequest,
  BiometricIdentityVerificationRequestStatus,
  PayerTaxInformationShareStatus
} from "@wingspanhq/wingspan-api-sdk";
import { useCallback } from "react";

import { footprintAppearanceConfig } from "../../../../../../shared/constants/footprint";
import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import { RequirementCardContent } from "../../../../components/RequirementCardContent";
import { RequirementInstructionsView } from "../../../../components/RequirementInstructionsView/RequirementInstructionsView";
import { selectorIsRequirementWithApprovalFlow } from "../../../../selectors/selectorIsRequirementWithApprovalFlow";
import {
  CellsConfig,
  ContextItem,
  RequirementAction,
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../../types";
import { ALL_REQUIREMENT_EVENTS } from "../../../../utils";
import { useModalShareInformationWithClient } from "../../components/ModalShareInformationWithClient/useModalFormW9Consent";
import { ValidationProgress } from "../../components/ValidationProgress";
import { IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS } from "../../instructions";
import { getIdentityVerificationPayeeRequirementStatus } from "../helpers/getIdentityVerificationPayeeRequirementStatus";
import { getIdentityVerificationPayerProgressSteps } from "../helpers/getIdentityVerificationPayerProgressSteps";
import { getIdentityVerificationRequirementName } from "../helpers/getIdentityVerificationRequirementName";
import { getRequirementPayerFromCache } from "../helpers/getRequirementPayerFromCache";
import {
  IDENTITY_VERIFICATION_IN_FLIGHT_STATUSES,
  useIDVPendingSuccessStatus
} from "../utils";

import { payerIdentityVerificationRenderCardDescription } from "./payerIdentityVerificationRenderCardDescription";

export const usePayerIdentityVerificationConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  RequirementTableItemData,
  ContextItem,
  BiometricIdentityVerificationRequest | undefined
> => {
  const shareInfoModal = useModalShareInformationWithClient();

  const {
    setPendingSuccessStatus,
    getIsPendingSuccessStatus: isPendingSuccess
  } = useIDVPendingSuccessStatus();

  const [startIdentityVerification, startIdentityVerificationMeta] =
    queriesBiometricIdentityVerifications.startBiometricIdentityVerification.useMutation();

  const openFootprint = useCallback(
    async (
      requirement: RequirementTableItemData,
      identityVerification: BiometricIdentityVerificationRequest,
      token: string
    ) => {
      const onClose = () => {
        console.log("IdentityVerification onClose");
      };

      const onCancel = () => {
        console.log("IdentityVerification onCancel");
      };

      const component = footprint.init({
        appearance: footprintAppearanceConfig,
        authToken: token,
        kind: FootprintComponentKind.Verify,
        variant: "modal",
        containerId: "root",
        onCancel: onCancel,
        onClose: onClose,
        options: {
          showLogo: false
        },
        onComplete: () => {
          setPendingSuccessStatus(requirement, identityVerification);
          console.log("IdentityVerification onComplete");
          callback(ALL_REQUIREMENT_EVENTS.CompleteVerification, requirement);
          component.destroy();
        }
      });

      component.render();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getConsentAction = (
    requirement: RequirementTableItemData,
    context: ContextItem,
    identityVerification: BiometricIdentityVerificationRequest | undefined
  ) => {
    const payer = getRequirementPayerFromCache(requirement, context);

    // v3: gate the consent action on the payer's tax-information-share status
    // (Payer.taxInformationShare replaces v1 payeeOwnedData.shareTaxDocument) —
    // only offer "consent to share" while it is not already shared.
    const isTaxInfoShared =
      payer?.taxInformationShare?.status ===
      PayerTaxInformationShareStatus.ACTIVE;

    if (
      IDENTITY_VERIFICATION_IN_FLIGHT_STATUSES.includes(
        identityVerification?.status
      ) &&
      payer &&
      !isTaxInfoShared
    ) {
      return async () => {
        await shareInfoModal.open({ payer });
      };
    }

    return undefined;
  };

  const getStartAction = (
    requirement: RequirementTableItemData,
    context: ContextItem,
    identityVerification: BiometricIdentityVerificationRequest | undefined
  ) => {
    const consentAction = getConsentAction(
      requirement,
      context,
      identityVerification
    );

    // The start call is keyed on the verification, which the request carries a
    // reference to — without it there is nothing to start.
    const biometricIdentityVerificationId =
      identityVerification?.biometricIdentityVerificationId;

    if (
      !consentAction &&
      biometricIdentityVerificationId &&
      !isPendingSuccess(requirement, identityVerification) &&
      IDENTITY_VERIFICATION_IN_FLIGHT_STATUSES.includes(
        identityVerification?.status
      )
    ) {
      return async () => {
        await startIdentityVerification(
          {
            biometricIdentityVerificationId
          },
          {
            onSuccess: session => {
              const token = session.sessionToken;

              if (!token) {
                return;
              }
              openFootprint(requirement, identityVerification!, token);
            }
          }
        );
      };
    }

    return undefined;
  };

  return {
    isActionRequired: (requirement, context, identityVerification) => {
      return isPendingSuccess(requirement, identityVerification)
        ? false
        : IDENTITY_VERIFICATION_IN_FLIGHT_STATUSES.includes(
            identityVerification?.status
          );
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      identityVerification
    ) => {
      return identityVerification?.status
        ? getIdentityVerificationPayerProgressSteps({
            status: identityVerification.status,
            isPendingSuccessStatus: isPendingSuccess(
              requirement,
              identityVerification
            ),
            isApprovalFlow: selectorIsRequirementWithApprovalFlow(requirement)
          })
        : undefined;
    },
    requirementStatusAlert: (requirement, context, identityVerification) => {
      if (
        IDENTITY_VERIFICATION_IN_FLIGHT_STATUSES.includes(
          identityVerification?.status
        )
      ) {
        return {
          theme: "warning",
          title: "Action required",
          children:
            "Complete the requirement in order to be eligible to receive payments from your client.",
          icon: "alert"
        };
      } else if (
        identityVerification?.status ===
        BiometricIdentityVerificationRequestStatus.FAILED
      ) {
        return {
          theme: "error",
          title: "Failed",
          children:
            "Something went wrong with your ID verification. Please contact your client to resolve the issue.",
          icon: "alert-circle"
        };
      }
    },
    requirementName: getIdentityVerificationRequirementName,
    requirementInstructions: () => {
      return () => {
        return (
          <RequirementInstructionsView
            instructions={IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS}
          />
        );
      };
    },
    requirementDetails: (requirement, context, identityVerification) => {
      const startAction = getStartAction(
        requirement,
        context,
        identityVerification
      );

      const consentAction = getConsentAction(
        requirement,
        context,
        identityVerification
      );

      const payer = getRequirementPayerFromCache(requirement, context);

      const isApprovalFlow = selectorIsRequirementWithApprovalFlow(requirement);

      return {
        header: {
          label: {
            text: "Identity verification"
          },
          value: {
            pill: isApprovalFlow
              ? undefined
              : identityVerification?.status ===
                  BiometricIdentityVerificationRequestStatus.FAILED
                ? {
                    theme: "error",
                    text: "Failed",
                    icon: true
                  }
                : undefined
          }
        },
        children: startIdentityVerificationMeta.isError ? (
          <RequirementCardContent
            description={
              <>
                Something went wrong while processing your verification. Please
                try again or contact support if this continues!
              </>
            }
            title="Something went wrong with your ID verification"
            type="people"
          />
        ) : isPendingSuccess(requirement, identityVerification) ? (
          <ValidationProgress
            identityVerification={identityVerification!}
            requirement={requirement}
            onSuccess={callback}
          />
        ) : (
          payerIdentityVerificationRenderCardDescription(
            requirement,
            identityVerification,
            payer
          )
        ),
        data: [],
        actions:
          startAction || consentAction
            ? {
                alignment: "fill",
                buttons: [
                  {
                    label: "Start ID verification",
                    onAsyncClick: startAction,
                    kind: "Primary",
                    visible: !!startAction
                  },
                  {
                    label: "Consent to share information",
                    onAsyncClick: consentAction,
                    kind: "Primary",
                    visible: !!consentAction
                  }
                ]
              }
            : undefined
      };
    },
    toolbarActions: (requirement, context, identityVerification) => {
      const actions: RequirementAction[] = [];

      const startAction = getStartAction(
        requirement,
        context,
        identityVerification
      );

      const consentAction = getConsentAction(
        requirement,
        context,
        identityVerification
      );

      if (consentAction) {
        actions.push({
          label: "Consent to share information",
          onAsyncClick: consentAction,
          buttonKind: "Primary",
          icon: "open-tab",
          buttonIcon: null,
          mainAction: true
        });
      }

      if (startAction) {
        actions.push({
          label: "Start ID verification",
          onAsyncClick: startAction,
          buttonKind: "Primary",
          icon: "open-tab",
          buttonIcon: null,
          mainAction: true
        });
      }

      return actions;
    },
    nameCell: (requirement, context, identityVerification) => {
      return {
        text: getIdentityVerificationRequirementName(
          requirement,
          context,
          identityVerification
        )
      };
    },
    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.type
      }),
    statusCell: (requirement, context, identityVerification) => {
      return getIdentityVerificationPayeeRequirementStatus(
        requirement,
        context,
        identityVerification
      );
    }
  };
};
